import { useState } from "react";
import { Shield, Globe, Bell, Instagram, Smartphone, ChevronRight, Check, Users, Clock, Share2, Fingerprint } from "lucide-react";

export default function Landing() {
  const [activeScreen, setActiveScreen] = useState(0);
  const screens = [
    { src: "/screenshot-scan.png", label: "Cerca profili" },
    { src: "/screenshot-profile-social.png", label: "Social collegati" },
    { src: "/screenshot-notifications.png", label: "Notifiche" },
    { src: "/screenshot-profile-traits.png", label: "Segni particolari" }
  ];

  return (
    <main data-testid="landing-page" className="pt-16 md:pt-20">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-400/10 rounded-full blur-[100px] pointer-events-none"></div>
        
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            {/* Left - Text */}
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-medium text-purple-300">
                  Social Discovery App
                </span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.1] mb-6">
                Connection<br />
                <span className="text-gradient">starts nearby</span>
              </h1>
              
              <p className="text-lg md:text-xl text-purple-200/60 leading-relaxed mb-8 max-w-lg">
                Scopri persone nelle tue vicinanze e connettiti attraverso i social media. 
                Un tap per trovare nuovi amici e scambiare Instagram, TikTok e molto altro.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://apps.apple.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-download-btn"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl hover:shadow-purple-500/30 transition-all hover:scale-105 active:scale-95"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  Scarica su App Store
                </a>
                <a
                  href="#social-discovery"
                  data-testid="hero-learn-more-btn"
                  className="inline-flex items-center justify-center gap-2 border-2 border-purple-500/50 text-white bg-transparent px-8 py-4 rounded-full font-bold text-lg hover:bg-purple-500/10 transition-all active:scale-95"
                >
                  Scopri i social
                  <ChevronRight className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Right - Phone Mockup with Real Screenshots */}
            <div className="relative animate-fade-in-up-delay-1 flex justify-center">
              <div className="relative">
                {/* Phone frame */}
                <div className="relative w-72 md:w-80 bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1a] rounded-[3rem] p-2 border border-purple-500/20 shadow-2xl shadow-purple-500/20 animate-float">
                  {/* Screen */}
                  <div className="bg-black rounded-[2.5rem] overflow-hidden">
                    <img
                      src={screens[activeScreen].src}
                      alt={screens[activeScreen].label}
                      className="w-full h-auto"
                    />
                  </div>
                </div>
                
                {/* Screen switcher */}
                <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 flex gap-2 flex-wrap justify-center max-w-sm">
                  {screens.map((screen, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveScreen(index)}
                      data-testid={`screen-switcher-${index}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        activeScreen === index
                          ? "bg-purple-600 text-white"
                          : "bg-purple-500/10 text-purple-300 hover:bg-purple-500/20"
                      }`}
                    >
                      {screen.label}
                    </button>
                  ))}
                </div>

                {/* Floating badges */}
                <div className="absolute -left-8 top-1/4 bg-[#1a1a2e]/90 backdrop-blur-xl border border-purple-500/20 rounded-2xl p-4 shadow-xl animate-fade-in-up-delay-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-orange-500 rounded-full flex items-center justify-center">
                      <Instagram className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">Social collegati</p>
                      <p className="text-purple-300/50 text-xs">Instagram, TikTok...</p>
                    </div>
                  </div>
                </div>

                <div className="absolute -right-4 top-1/2 bg-[#1a1a2e]/90 backdrop-blur-xl border border-purple-500/20 rounded-2xl p-4 shadow-xl animate-fade-in-up-delay-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">Raggio 50m</p>
                      <p className="text-purple-300/50 text-xs">Cerca profili</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Discovery Section - NEW FOCUS */}
      <section id="social-discovery" className="py-20 md:py-32 bg-gradient-to-b from-purple-950/30 to-transparent">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider mb-4 block">
              Social Discovery
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4">
              Scopri i <span className="text-gradient">social</span> di chi ti circonda
            </h2>
            <p className="text-purple-200/50 text-lg max-w-2xl mx-auto">
              Collega i tuoi profili social e scopri quelli delle persone vicino a te. 
              Instagram, TikTok, Facebook, X e LinkedIn - tutto in un tap.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left - Screenshot Profile Social */}
            <div className="flex justify-center">
              <div className="w-64 md:w-72 bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1a] rounded-[2.5rem] p-2 border border-purple-500/20 shadow-2xl shadow-purple-500/30">
                <img
                  src="/screenshot-profile-social.png"
                  alt="Social collegati"
                  className="w-full h-auto rounded-[2rem]"
                />
              </div>
            </div>

            {/* Right - Features */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Instagram className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Collega i tuoi social</h3>
                  <p className="text-purple-200/50">
                    Instagram, TikTok, Facebook, X e LinkedIn. 
                    Scegli quali mostrare e a chi.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-purple-400 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Share2 className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Controllo visibilità</h3>
                  <p className="text-purple-200/50">
                    Profilo Pubblico o Privato: decidi tu chi può vedere 
                    i tuoi link social.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-400 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Check className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Connessione istantanea</h3>
                  <p className="text-purple-200/50">
                    Accetta una richiesta e scambia subito i profili social 
                    con nuovi amici.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="come-funziona" className="py-20 md:py-32 relative">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider mb-4 block">
              Come funziona
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4">
              Semplice come un <span className="text-gradient">tap</span>
            </h2>
            <p className="text-purple-200/50 text-lg max-w-2xl mx-auto">
              Connettiti con persone vicine e scambia i social in pochi secondi
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 md:gap-8">
            {[
              { 
                step: "01", 
                icon: Smartphone, 
                title: "Crea il profilo", 
                desc: "Aggiungi foto, bio e collega Instagram, TikTok e gli altri social"
              },
              { 
                step: "02", 
                icon: "scan", 
                title: "Scansiona", 
                desc: "Premi il pulsante centrale per cercare utenti entro 50 metri"
              },
              { 
                step: "03", 
                icon: Users, 
                title: "Scopri", 
                desc: "Visualizza i profili e invia richieste di connessione"
              },
              { 
                step: "04", 
                icon: Instagram, 
                title: "Scambia i social", 
                desc: "Connettiti e scopri i profili Instagram, TikTok e altro"
              },
            ].map((item, index) => (
              <div 
                key={index} 
                data-testid={`how-it-works-step-${item.step}`}
                className="relative group"
              >
                <div className="bg-purple-950/30 backdrop-blur-sm border border-purple-500/10 rounded-2xl p-6 md:p-8 hover:border-purple-500/30 transition-all hover:bg-purple-950/50 h-full">
                  <span className="text-5xl font-extrabold text-purple-500/20 absolute top-4 right-4">
                    {item.step}
                  </span>
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-purple-400 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/20">
                    {item.icon === "scan" ? (
                      <img src="/nerbys-logo.png" alt="Scan" className="w-8 h-8" />
                    ) : (
                      <item.icon className="w-7 h-7 text-white" />
                    )}
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-white">
                    {item.title}
                  </h3>
                  <p className="text-purple-200/50 text-sm">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Screenshots Gallery - All 4 Screenshots */}
      <section className="py-20 md:py-32 bg-purple-950/20">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider mb-4 block">
              L'app
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4">
              Scopri <span className="text-gradient">Nerbys</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { src: "/screenshot-scan.png", label: "Cerca profili", rotate: "-rotate-3" },
              { src: "/screenshot-profile-social.png", label: "Social collegati", rotate: "rotate-2" },
              { src: "/screenshot-notifications.png", label: "Notifiche", rotate: "-rotate-2" },
              { src: "/screenshot-profile-traits.png", label: "Segni particolari", rotate: "rotate-3" }
            ].map((screen, index) => (
              <div 
                key={index}
                className={`transform ${screen.rotate} hover:rotate-0 transition-transform duration-300`}
              >
                <div className="bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1a] rounded-[1.5rem] md:rounded-[2rem] p-1 md:p-1.5 border border-purple-500/20 shadow-xl">
                  <img
                    src={screen.src}
                    alt={screen.label}
                    className="w-full h-auto rounded-[1.25rem] md:rounded-[1.5rem]"
                  />
                </div>
                <p className="text-center text-purple-300 text-xs md:text-sm mt-3 font-medium">{screen.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Profile Customization Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left - Features */}
            <div>
              <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider mb-4 block">
                Personalizzazione
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-6">
                Fatti <span className="text-gradient">riconoscere</span>
              </h2>
              <p className="text-purple-200/50 text-lg mb-8">
                Aggiungi segni particolari al tuo profilo per facilitare il riconoscimento 
                quando incontri qualcuno nelle vicinanze.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: Fingerprint, text: "Segni distintivi personalizzabili" },
                  { icon: Users, text: "Descrivi il tuo aspetto generale" },
                  { icon: Clock, text: "Stato 'In questo momento' (scade in 4h)" },
                  { icon: Shield, text: "Controllo totale sulla privacy" },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-purple-400" />
                    </div>
                    <p className="text-purple-200/70">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Screenshot */}
            <div className="flex justify-center">
              <div className="w-64 md:w-72 bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1a] rounded-[2.5rem] p-2 border border-purple-500/20 shadow-2xl shadow-purple-500/30">
                <img
                  src="/screenshot-profile-traits.png"
                  alt="Segni particolari"
                  className="w-full h-auto rounded-[2rem]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 md:py-32 bg-purple-950/20">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider mb-4 block">
              Funzionalità
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4">
              Tutto ciò di cui hai <span className="text-gradient">bisogno</span>
            </h2>
          </div>

          {/* Bento Grid */}
          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {/* Feature 1 - Large */}
            <div 
              data-testid="feature-proximity"
              className="md:col-span-2 bg-gradient-to-br from-purple-900/30 to-purple-950/50 border border-purple-500/10 rounded-3xl p-8 md:p-10 hover:border-purple-500/30 transition-all group"
            >
              <div className="flex items-start gap-5">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-400 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/30">
                  <img src="/nerbys-logo.png" alt="Nerbys" className="w-10 h-10" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3 text-white">
                    Scansione Proximity-Based
                  </h3>
                  <p className="text-purple-200/50 leading-relaxed">
                    Premi il pulsante centrale per cercare utenti nelle vicinanze. 
                    Raggio di ricerca di 50 metri con feedback sonoro e vibrazione.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div 
              data-testid="feature-social"
              className="bg-purple-950/30 border border-purple-500/10 rounded-3xl p-8 hover:border-purple-500/30 transition-all group"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/20">
                <Instagram className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">
                5 Social Media
              </h3>
              <p className="text-purple-200/50 text-sm">
                Instagram, Facebook, TikTok, X e LinkedIn con controllo visibilità.
              </p>
            </div>

            {/* Feature 3 */}
            <div 
              data-testid="feature-privacy"
              className="bg-purple-950/30 border border-purple-500/10 rounded-3xl p-8 hover:border-purple-500/30 transition-all group"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-400 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/20">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">
                Privacy & Sicurezza
              </h3>
              <p className="text-purple-200/50 text-sm">
                Verifica AI delle foto e controllo totale sulla visibilità.
              </p>
            </div>

            {/* Feature 4 - Large */}
            <div 
              data-testid="feature-connections"
              className="md:col-span-2 bg-gradient-to-br from-purple-900/30 to-purple-950/50 border border-purple-500/10 rounded-3xl p-8 md:p-10 hover:border-purple-500/30 transition-all group"
            >
              <div className="flex items-start gap-5">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/30">
                  <Bell className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3 text-white">
                    Connessioni Smart
                  </h3>
                  <p className="text-purple-200/50 leading-relaxed">
                    Profili Pubblici e Privati. Notifiche push istantanee: 
                    "Qualcuno ti sorride..." quando ricevi una richiesta.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 5 - Multilang */}
            <div 
              data-testid="feature-multilang"
              className="md:col-span-3 bg-purple-950/30 border border-purple-500/10 rounded-3xl p-8 hover:border-purple-500/30 transition-all"
            >
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Globe className="w-8 h-8 text-purple-400" />
                <span className="text-xl font-bold text-white">Multilingua:</span>
                <div className="flex gap-3 flex-wrap justify-center">
                  {["Italiano", "English", "Español", "Français", "Deutsch"].map((lang) => (
                    <span key={lang} className="px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full text-sm text-purple-300">
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider mb-4 block">
                Sicurezza
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-6">
                La tua sicurezza è la nostra <span className="text-gradient">priorità</span>
              </h2>
              <div className="space-y-4">
                {[
                  { icon: Shield, text: "Verifica foto profilo tramite AI" },
                  { icon: Users, text: "Sistema di segnalazione e blocco" },
                  { icon: Bell, text: "Warning per comportamenti inappropriati" },
                  { icon: Check, text: "Controllo totale sulla visibilità" },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-purple-400" />
                    </div>
                    <p className="text-purple-200/70">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-900/30 to-purple-950/50 border border-purple-500/20 rounded-3xl p-8 md:p-12">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-400 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-green-500/30">
                  <Shield className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Profilo Verificato</h3>
                <p className="text-purple-200/50 mb-6">
                  Ogni foto viene verificata dalla nostra AI per garantire autenticità.
                </p>
                <div className="flex justify-center gap-2 flex-wrap">
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">GDPR</span>
                  <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">17+</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-600/10 to-transparent pointer-events-none"></div>
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 text-center relative">
          <div className="max-w-2xl mx-auto">
            <img 
              src="/nerbys-logo.png" 
              alt="Nerbys" 
              className="w-20 h-20 mx-auto mb-8 rounded-2xl shadow-xl shadow-purple-500/30"
            />
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-6">
              Pronto a scoprire i <span className="text-gradient">social</span> di chi ti circonda?
            </h2>
            <p className="text-purple-200/50 text-lg mb-8">
              Scarica Nerbys e inizia a connetterti con persone nelle tue vicinanze.
            </p>
            <a
              href="https://apps.apple.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-download-btn"
              className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-10 py-5 rounded-full font-bold text-lg hover:shadow-xl hover:shadow-purple-500/30 transition-all hover:scale-105 active:scale-95"
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              Scarica Gratis su App Store
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
